import { useEffect, useState } from 'react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

/**
 * In-page table of contents for an editorial post. Scans the rendered `.blog-prose`
 * headings on mount and tracks the active section with an IntersectionObserver.
 * Renders nothing until hydration or when a post has fewer than two headings.
 */
export const BlogToc = ({ variant = 'sidebar' }: { variant?: 'sidebar' | 'inline' }) => {
  const [items, setItems] = useState<Heading[]>([]);
  const [active, setActive] = useState('');

  useEffect(() => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>('.blog-prose h2[id], .blog-prose h3[id]'),
    );
    setItems(
      nodes.map((node) => ({
        id: node.id,
        text: node.textContent?.trim() ?? '',
        level: node.tagName === 'H3' ? 3 : 2,
      })),
    );
    if (nodes.length < 2) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive((entry.target as HTMLElement).id);
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  if (items.length < 2) return null;

  const inline = variant === 'inline';
  return (
    <nav
      aria-label="Table of contents"
      className={`text-13 ${inline ? 'rounded-12 border border-rule px-5 py-4' : ''}`}
    >
      <div className="mb-3 text-13 font-medium text-text-3">On this page</div>
      <ul className="flex flex-col gap-2 border-l border-rule">
        {items.map((heading) => (
          <li key={heading.id} style={{ paddingLeft: heading.level === 3 ? 24 : 12 }}>
            <a
              href={`#${heading.id}`}
              className={`-ml-px block border-l-2 pl-3 leading-snug transition-colors ${
                active === heading.id
                  ? 'border-accent text-text'
                  : 'border-transparent text-text-3 hover:text-text-2'
              }`}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};
